// ===== MessageReactions: 이모지 반응 피커 · 칩 · 실시간 · 복원 (Phase 10, bigtionax-chat-mention-reply-reaction) =====
// P9(routes/rooms.ts)가 저장소·인가·엔드포인트(PUT/GET .../reactions)를 이미 끝내 놓았다 — 이 모듈은
// 그 위에 피커·칩·낙관적 갱신·실시간 반영·복원만 얹는다. classic script 전역 IIFE
// (work-badge.ts/work-inline-cards.ts와 동일한 형태) — export/require 금지(frontend-compiled-assets
// 컴파일 산출물 계약).
//
// 페이로드 계약(§설계C, P9 실제 응답 필드 기준):
// - PUT 응답·GET 범위 조회 응답은 뷰어 상대 {emoji, count, mine}을 싣는다(actorId가 요청에 실리므로
//   서버가 정확히 계산해 준다) — 이 값은 그대로 신뢰해 치환한다.
// - WS 실시간 브로드캐스트("message-reaction")는 {emoji, count}만 싣는다. mine은 싣지 않는다 — 브리지가
//   하나의 payload를 모든 수신자에게 그대로 보내므로, 토글한 actorId 기준 mine을 실으면 다른 수신자
//   화면에 "본인이 반응한 적 없는 이모지"가 mine=true로 잘못 표시된다(routes/rooms.ts emitMessageReaction
//   참고). 그래서 실시간 이벤트는 이미 알고 있는 로컬 mine을 유지하고, 처음 보는 이모지는 mine=false로
//   시작한다 — 내 자신의 토글 결과는 이 이벤트가 아니라 PUT 응답(mine 포함)으로 이미 확정돼 있으므로
//   정확도 손실이 없다.
// - 모든 경로가 내부적으로 동일 표현 {emoji, count, mine}으로 정규화된 뒤 같은 렌더 함수를 탄다.
//
// 자기 에코가 이중 계산되는 사고(FP24)는 "이벤트 경로에서 count ± 1 증감"을 쓸 때만 발생한다 — 이
// 모듈의 이벤트/응답 적용은 전부 치환이다. 사용자 클릭 시의 로컬 낙관적 예측(±1)은 이벤트 경로가
// 아니라 응답이 오기 전까지의 임시 렌더일 뿐이며, 응답 도착 즉시 권위값으로 치환되므로 이중 계산과
// 무관하다.

type MrParticipantEntry = { displayName: string };
type MrReactionEntry = {
  emoji: string;
  count: number;
  mine?: boolean;
  participants?: MrParticipantEntry[];
  hiddenCount?: number;
};
type MrReactionState = { count: number; mine: boolean; participants: MrParticipantEntry[]; hiddenCount: number };
type MrKeyState = { reactions: Map<string, MrReactionState>; lastLiveStamp: number };
type MrActivePicker = { sessionId: string; seq: number; anchorBtn: HTMLElement; messageEl: HTMLElement } | null;
type MrActiveParticipantPopover = {
  sessionId: string;
  seq: number;
  emoji: string;
  anchorEl: HTMLElement;
  messageEl: HTMLElement;
  requestId: number;
  restoreFocus: boolean;
} | null;
type MrLongPressState = {
  anchorEl: HTMLElement;
  messageEl: HTMLElement;
  sessionId: string;
  seq: number;
  emoji: string;
  pointerId: number | null;
  startX: number;
  startY: number;
  timerId: number;
} | null;
type MrSuppressedClick = { key: string; remaining: number } | null;
type MrPendingFocus = { key: string } | null;

var MessageReactions = (function () {
  "use strict";

  // D03: 서버가 소유하는 고정 12종(routes/rooms.ts REACTION_EMOJIS)과 동일해야 한다 — 드리프트는
  // reaction-realtime.test.ts가 실제 라우트 왕복으로 잡는다(§설계D).
  var REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🎉", "🔥", "👀", "✅", "💡", "😅"];

  // P9 범위 읽기 상한(routes/rooms.ts MAX_REACTION_RANGE_SPAN)과 동일 — 이 상한을 넘는 구간은
  // 이 값 단위로 잘라 순차 조회한다(§설계H).
  var RANGE_CHUNK = 200;

  // 실패 시 그 세션 전체를 비활성화하는 상태 코드(§설계F): 503(roomStore 비활성) · P9 no-room 4xx
  // (404 room_not_found_for_session · 403 room_membership_required).
  var DISABLING_STATUSES = [403, 404, 503];

  var state = new Map<string, MrKeyState>();
  var disabledSessions = new Set<string>();
  var liveStamp = 0;
  var activePicker: MrActivePicker = null;
  var activeParticipantPopover: MrActiveParticipantPopover = null;
  var participantRequestSeq = 0;
  var longPressState: MrLongPressState = null;
  var suppressedClick: MrSuppressedClick = null;
  var suppressParticipantFocusOpen = false;
  var pendingParticipantFocus: MrPendingFocus = null;
  var participantFocusTransferKey: string | null = null;
  var pickerBuilt = false;
  var longPressThresholdPx = 8;
  var longPressDelayMs = 500;

  function _isMrRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  // filter(_isMrReactionEntry)가 곧바로 MrReactionEntry[]를 돌려주도록 타입 가드로 선언한다
  // (§설계B "런타임 가드를 통과한 배열") — _replaceReactions의 emoji/count 검증과 같은 조건이므로
  // 실제로 걸러지는 항목 집합은 이전과 동일하다.
  function _isMrReactionEntry(value: unknown): value is MrReactionEntry {
    return _isMrRecord(value) && typeof value.emoji === "string" && typeof value.count === "number";
  }

  function _makeKey(sessionId: string, seq: number): string {
    return sessionId + "#" + seq;
  }

  function _ensureState(key: string): MrKeyState {
    var existing = state.get(key);
    if (existing) return existing;
    var created: MrKeyState = { reactions: new Map(), lastLiveStamp: 0 };
    state.set(key, created);
    return created;
  }

  // work-badge.ts _fetchJson/work-inline-cards.ts _fetchJson 미러 — 서버 에러 메시지·statusCode를
  // 그대로 전달해 호출자가 403/404/503 비활성화 판정을 할 수 있게 한다.
  async function _fetchJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
    var response = await fetch(url, options);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!_isMrRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      var requestError = new Error(typeof payload.error === "string" ? payload.error : "Request failed: " + response.status) as Error & { statusCode?: number };
      requestError.statusCode = response.status;
      throw requestError;
    }
    return payload;
  }

  function _chatMessagesEl(): HTMLElement | null {
    return document.getElementById("chatMessages");
  }

  function _chatAreaEl(): HTMLElement | null {
    return document.querySelector<HTMLElement>(".chat-area");
  }

  function _pickerEl(): HTMLElement | null {
    return document.getElementById("reactionPicker");
  }

  function _participantPopoverEl(): HTMLElement | null {
    return document.getElementById("reactionParticipantPopover");
  }

  function _ensureParticipantPopover(): HTMLElement | null {
    var existing = _participantPopoverEl();
    if (existing) return existing;
    var area = _chatAreaEl();
    if (!area) return null;
    var popover = document.createElement("div");
    popover.id = "reactionParticipantPopover";
    popover.className = "reaction-participant-popover";
    popover.setAttribute("role", "dialog");
    popover.setAttribute("aria-label", "반응 참여자");
    popover.hidden = true;
    area.appendChild(popover);
    return popover;
  }

  function _isMrParticipantEntry(value: unknown): value is MrParticipantEntry {
    return _isMrRecord(value) && typeof value.displayName === "string";
  }

  // ===== 상태 치환(멱등) =====

  // mineKnown=true: entries의 mine을 그대로 신뢰(PUT/GET 응답 — actorId 기준으로 서버가 계산).
  // mineKnown=false: entries에 mine이 없다(실시간 브로드캐스트) — 이미 알던 emoji는 로컬 mine을
  // 보존하고, 처음 보는 emoji는 mine=false로 시작한다. entries에 없는 기존 emoji는 제거한다(§설계C
  // "칩 묶음을 통째로 치환").
  function _replaceReactions(st: MrKeyState, entries: MrReactionEntry[], mineKnown: boolean): void {
    var next = new Map<string, MrReactionState>();
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry || typeof entry.emoji !== "string" || typeof entry.count !== "number") continue;
      var prev = st.reactions.get(entry.emoji);
      var mine = mineKnown ? entry.mine === true : (prev ? prev.mine : false);
      var participants = Array.isArray(entry.participants) ? entry.participants.filter(_isMrParticipantEntry) : [];
      var hiddenCount = typeof entry.hiddenCount === "number" && entry.hiddenCount > 0 ? entry.hiddenCount : 0;
      next.set(entry.emoji, { count: entry.count, mine: mine, participants: participants, hiddenCount: hiddenCount });
    }
    st.reactions = next;
  }

  // 실시간 fan-out 적용 — 항상 즉시 적용되고, 전역 stamp를 올려 "이 시점 이후"를 표시한다.
  // 게이트(다른 세션 무렌더)는 ws-handlers.ts의 onMessageReaction이 이미 통과시킨 뒤 위임한다.
  // P13(bigtionax-chat-mention-reply-reaction, D10): 와이어 최상위 앵커는 logSeq다(routes/rooms.ts
  // emitMessageReaction) — REST 필드 seq는 그대로 두고 이 실시간 이벤트만 분리했다.
  function onMessageReaction(data: unknown): void {
    if (!_isMrRecord(data) || typeof data.sessionId !== "string" || !Number.isInteger(data.logSeq)) return;
    var sessionId = data.sessionId;
    var seq = data.logSeq as number;
    if (disabledSessions.has(sessionId)) return;
    var entries: MrReactionEntry[] = Array.isArray(data.reactions) ? data.reactions.filter(_isMrReactionEntry) : [];

    liveStamp += 1;
    var key = _makeKey(sessionId, seq);
    var st = _ensureState(key);
    st.lastLiveStamp = liveStamp;
    _replaceReactions(st, entries, false);
    _renderChips(sessionId, seq);
  }

  // 응답(PUT 성공 · GET 범위 조회 · 롤백)을 적용하되, 요청을 띄운 이후 그 메시지에 더 새로운
  // fan-out이 이미 적용됐으면(stale-write 가드, §설계H) 건너뛴다 — 남의 최신 상태를 낡은 응답으로
  // 덮지 않는다.
  function _applyIfFresh(sessionId: string, seq: number, issuedAt: number, entries: MrReactionEntry[], mineKnown: boolean): void {
    var key = _makeKey(sessionId, seq);
    var st = _ensureState(key);
    if (st.lastLiveStamp > issuedAt) return;
    _replaceReactions(st, entries, mineKnown);
    _renderChips(sessionId, seq);
  }

  // ===== 렌더 =====

  function _findMessageEl(sessionId: string, seq: number): HTMLElement | null {
    var container = _chatMessagesEl();
    if (!container) return null;
    var nodes = container.querySelectorAll<HTMLElement>('[data-msg-seq="' + seq + '"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.sessionId === sessionId) return nodes[i];
    }
    return null;
  }

  function _findReactionChip(sessionId: string, seq: number, emoji: string): HTMLElement | null {
    var messageEl = _findMessageEl(sessionId, seq);
    if (!messageEl) return null;
    var chips = messageEl.querySelectorAll<HTMLElement>(".msg-reaction");
    for (var i = 0; i < chips.length; i++) {
      if (chips[i].dataset.emoji === emoji) return chips[i];
    }
    return null;
  }

  function _applyPendingParticipantFocus(sessionId: string, seq: number): void {
    if (!pendingParticipantFocus) return;
    var prefix = sessionId + "#" + seq + "#";
    if (pendingParticipantFocus.key.indexOf(prefix) !== 0) return;
    var focusKey = pendingParticipantFocus.key;
    var emoji = focusKey.slice(prefix.length);
    var chip = _findReactionChip(sessionId, seq, emoji);
    pendingParticipantFocus = null;
    if (!chip) return;
    participantFocusTransferKey = focusKey;
    suppressParticipantFocusOpen = true;
    chip.focus();
    queueMicrotask(function () {
      if (participantFocusTransferKey === focusKey) participantFocusTransferKey = null;
    });
  }

  function _renderChips(sessionId: string, seq: number): void {
    var messageEl = _findMessageEl(sessionId, seq);
    var slot = messageEl ? messageEl.querySelector<HTMLElement>(".msg-reactions") : null;
    if (!slot) return;
    var st = state.get(_makeKey(sessionId, seq));
    var entries = st ? Array.from(st.reactions.entries()) : [];
    entries.sort(function (a, b) { return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0; });
    var disabled = disabledSessions.has(sessionId);
    slot.innerHTML = entries.map(function (pair) {
      var emoji = pair[0];
      var info = pair[1];
      var mineClass = info.mine ? " mine" : "";
      var isExpanded = !!(
        activeParticipantPopover
        && activeParticipantPopover.sessionId === sessionId
        && activeParticipantPopover.seq === seq
        && activeParticipantPopover.emoji === emoji
      );
      var disabledAttr = disabled ? ' aria-disabled="true"' : "";
      return '<button type="button" class="msg-reaction' + mineClass + '" data-emoji="' + escapeHtml(emoji) + '" aria-haspopup="dialog" aria-expanded="' + (isExpanded ? "true" : "false") + '"' + disabledAttr + '>'
        + '<span>' + emoji + '</span><span class="msg-reaction-count">' + info.count + '</span></button>';
    }).join("");
    if (
      activeParticipantPopover
      && activeParticipantPopover.sessionId === sessionId
      && activeParticipantPopover.seq === seq
    ) {
      var chip = _findReactionChip(sessionId, seq, activeParticipantPopover.emoji);
      if (!chip) {
        _closeParticipantPopover(false);
      } else {
        activeParticipantPopover.anchorEl = chip;
        activeParticipantPopover.messageEl = messageEl as HTMLElement;
        _positionParticipantPopover(chip);
      }
    }
    _applyPendingParticipantFocus(sessionId, seq);
  }

  // ===== 비활성 어포던스 (§설계F) =====

  function _markDisabled(sessionId: string): void {
    if (disabledSessions.has(sessionId)) return;
    disabledSessions.add(sessionId);
    var container = _chatMessagesEl();
    if (!container) return;
    var nodes = container.querySelectorAll<HTMLElement>(".message[data-session-id][data-msg-seq]");
    nodes.forEach(function (node) {
      if (node.dataset.sessionId !== sessionId) return;
      var trigger = node.querySelector<HTMLElement>('.msg-act[data-act="reaction"]');
      if (trigger) trigger.style.display = "none";
      node.querySelectorAll<HTMLElement>(".msg-reaction").forEach(function (chip) {
        chip.setAttribute("aria-disabled", "true");
      });
    });
  }

  function _maybeDisableFromStatus(sessionId: string, status: number | undefined): void {
    if (typeof status === "number" && DISABLING_STATUSES.indexOf(status) !== -1) _markDisabled(sessionId);
  }

  // _fetchJson이 던지는 에러에서 statusCode를 꺼낸다 — requestReactionToggle/_fetchRangeAndApply
  // 두 catch 블록이 동일하게 필요로 하는 추출 로직을 공용화한다.
  function _statusCodeOf(error: unknown): number | undefined {
    return error instanceof Error ? (error as Error & { statusCode?: number }).statusCode : undefined;
  }

  function _participantSummary(sessionId: string, seq: number, emoji: string): MrReactionState | null {
    var st = state.get(_makeKey(sessionId, seq));
    return st ? st.reactions.get(emoji) || null : null;
  }

  function _popoverStateRow(className: string, text: string): HTMLElement {
    var row = document.createElement("div");
    row.className = className;
    row.textContent = text;
    return row;
  }

  function _positionParticipantPopover(anchorEl: HTMLElement): void {
    var popover = _participantPopoverEl();
    var area = _chatAreaEl();
    if (!popover || !area) return;
    var areaRect = area.getBoundingClientRect();
    var anchorRect = anchorEl.getBoundingClientRect();
    var left = anchorRect.left - areaRect.left;
    left = Math.max(8, Math.min(left, areaRect.width - popover.offsetWidth - 8));
    var top = anchorRect.top - areaRect.top - popover.offsetHeight - 8;
    if (top < 8) top = anchorRect.bottom - areaRect.top + 8;
    popover.style.left = left + "px";
    popover.style.top = top + "px";
  }

  function _renderParticipantPopoverState(kind: string, data?: MrReactionState | null): void {
    var popover = _ensureParticipantPopover();
    if (!popover || !activeParticipantPopover) return;
    popover.innerHTML = "";

    var title = document.createElement("div");
    title.className = "reaction-participant-popover-title";
    title.textContent = activeParticipantPopover.emoji + " 참여자";
    popover.appendChild(title);

    if (kind === "loading") {
      popover.appendChild(_popoverStateRow("reaction-participant-popover-state", "참여자를 불러오는 중…"));
    } else if (kind === "forbidden") {
      popover.appendChild(_popoverStateRow("reaction-participant-popover-state", "현재 참여자를 확인할 수 없어요."));
    } else if (kind === "missing") {
      popover.appendChild(_popoverStateRow("reaction-participant-popover-state", "이 반응은 더 이상 표시할 참여자가 없어요."));
    } else if (kind === "error") {
      popover.appendChild(_popoverStateRow("reaction-participant-popover-state", "참여자를 불러오지 못했어요."));
      var retry = document.createElement("button");
      retry.type = "button";
      retry.className = "reaction-participant-popover-retry";
      retry.textContent = "다시 시도";
      retry.addEventListener("click", function () {
        if (!activeParticipantPopover) return;
        void _refreshParticipantPopover(activeParticipantPopover);
      });
      popover.appendChild(retry);
    } else {
      var summary = data || _participantSummary(
        activeParticipantPopover.sessionId,
        activeParticipantPopover.seq,
        activeParticipantPopover.emoji,
      );
      var participants = summary ? summary.participants : [];
      var hiddenCount = summary ? summary.hiddenCount : 0;
      if ((!participants || participants.length === 0) && hiddenCount === 0) {
        popover.appendChild(_popoverStateRow("reaction-participant-popover-state", "표시할 참여자가 아직 없어요."));
      } else {
        if (participants && participants.length > 0) {
          var list = document.createElement("div");
          list.className = "reaction-participant-popover-list";
          for (var i = 0; i < participants.length; i++) {
            var item = document.createElement("div");
            item.className = "reaction-participant-popover-name";
            item.textContent = participants[i].displayName;
            list.appendChild(item);
          }
          popover.appendChild(list);
        }
        if (hiddenCount > 0) {
          popover.appendChild(_popoverStateRow("reaction-participant-popover-hidden", "현재 참여자가 아닌 " + hiddenCount + "명"));
        }
      }
    }

    popover.hidden = false;
    activeParticipantPopover.anchorEl.setAttribute("aria-expanded", "true");
    _positionParticipantPopover(activeParticipantPopover.anchorEl);
  }

  function _closeParticipantPopover(restoreFocus: boolean): void {
    var popover = _participantPopoverEl();
    if (popover) {
      popover.hidden = true;
      popover.innerHTML = "";
    }
    var focusRestore: { sessionId: string; seq: number; emoji: string } | null = null;
    if (activeParticipantPopover) {
      activeParticipantPopover.anchorEl.setAttribute("aria-expanded", "false");
      if (restoreFocus && activeParticipantPopover.restoreFocus) {
        focusRestore = {
          sessionId: activeParticipantPopover.sessionId,
          seq: activeParticipantPopover.seq,
          emoji: activeParticipantPopover.emoji,
        };
      }
    }
    activeParticipantPopover = null;
    pendingParticipantFocus = null;
    participantFocusTransferKey = null;
    if (focusRestore) {
      var focusRestoreSessionId = focusRestore.sessionId;
      var focusRestoreSeq = focusRestore.seq;
      var focusRestoreEmoji = focusRestore.emoji;
      queueMicrotask(function () {
        var liveFocusTarget = _findReactionChip(focusRestoreSessionId, focusRestoreSeq, focusRestoreEmoji);
        if (!liveFocusTarget || !liveFocusTarget.isConnected) return;
        suppressParticipantFocusOpen = true;
        liveFocusTarget.focus();
      });
    }
  }

  function _participantPopoverStillValid(requestId: number, sessionId: string, seq: number, emoji: string): boolean {
    if (!activeParticipantPopover) return false;
    if (activeParticipantPopover.requestId !== requestId) return false;
    if (activeParticipantPopover.sessionId !== sessionId || activeParticipantPopover.seq !== seq || activeParticipantPopover.emoji !== emoji) return false;
    if (typeof window !== "undefined" && window.currentSessionId !== sessionId) return false;
    if (!activeParticipantPopover.anchorEl.isConnected) return false;
    return !!_findReactionChip(sessionId, seq, emoji);
  }

  async function _refreshParticipantPopover(openState: NonNullable<MrActiveParticipantPopover>): Promise<void> {
    _renderParticipantPopoverState("loading");
    try {
      var body = await _fetchJson(
        "/api/rooms/sessions/" + encodeURIComponent(openState.sessionId) + "/reactions?fromSeq=" + openState.seq + "&toSeq=" + openState.seq,
      );
      if (!_participantPopoverStillValid(openState.requestId, openState.sessionId, openState.seq, openState.emoji)) return;
      var messages = Array.isArray(body.messages) ? body.messages.filter(_isMrRecord) : [];
      var summary: MrReactionEntry | null = null;
      for (var i = 0; i < messages.length; i++) {
        var message = messages[i];
        if (message.seq !== openState.seq) continue;
        var reactions = Array.isArray(message.reactions) ? message.reactions.filter(_isMrReactionEntry) : [];
        for (var j = 0; j < reactions.length; j++) {
          if (reactions[j].emoji === openState.emoji) {
            summary = reactions[j];
            break;
          }
        }
      }
      if (!_participantPopoverStillValid(openState.requestId, openState.sessionId, openState.seq, openState.emoji)) return;
      if (!summary) {
        _renderParticipantPopoverState("missing");
        return;
      }
      var participantState: MrReactionState = {
        count: summary.count,
        mine: summary.mine === true,
        participants: Array.isArray(summary.participants) ? summary.participants.filter(_isMrParticipantEntry) : [],
        hiddenCount: typeof summary.hiddenCount === "number" && summary.hiddenCount > 0 ? summary.hiddenCount : 0,
      };
      var key = _makeKey(openState.sessionId, openState.seq);
      var st = _ensureState(key);
      var prev = st.reactions.get(openState.emoji);
      st.reactions.set(openState.emoji, {
        count: summary.count,
        mine: prev ? prev.mine : participantState.mine,
        participants: participantState.participants,
        hiddenCount: participantState.hiddenCount,
      });
      _renderChips(openState.sessionId, openState.seq);
      _renderParticipantPopoverState("ready", _participantSummary(openState.sessionId, openState.seq, openState.emoji));
    } catch (error: unknown) {
      var statusCode = _statusCodeOf(error);
      _maybeDisableFromStatus(openState.sessionId, statusCode);
      if (!_participantPopoverStillValid(openState.requestId, openState.sessionId, openState.seq, openState.emoji)) return;
      if (statusCode === 403 || statusCode === 404) {
        _renderParticipantPopoverState("forbidden");
        return;
      }
      _renderParticipantPopoverState("error");
    }
  }

  function openReactionParticipantPopover(messageEl: HTMLElement, anchorEl: HTMLElement, emoji: string, restoreFocus: boolean): void {
    var sessionId = messageEl.dataset.sessionId;
    var seqRaw = messageEl.dataset.msgSeq;
    var seq = seqRaw !== undefined ? Number(seqRaw) : NaN;
    if (!sessionId || !Number.isInteger(seq) || !emoji) return;
    if (disabledSessions.has(sessionId)) return;
    _closeParticipantPopover(false);
    // focus transfer intent는 "현재 focused chip이 곧 렌더로 교체되는가"에 대한 상태이고,
    // restoreFocus는 Escape/dismiss 시 그 focus를 다시 돌릴지에 대한 정책이다.
    if (document.activeElement === anchorEl) {
      pendingParticipantFocus = { key: _suppressedClickKey(sessionId, seq, emoji) };
    }
    activeParticipantPopover = {
      sessionId: sessionId,
      seq: seq,
      emoji: emoji,
      anchorEl: anchorEl,
      messageEl: messageEl,
      requestId: participantRequestSeq += 1,
      restoreFocus: restoreFocus,
    };
    var openState = activeParticipantPopover;
    _renderParticipantPopoverState("loading");
    void _refreshParticipantPopover(openState);
  }

  function _cancelLongPress(): void {
    if (!longPressState) return;
    clearTimeout(longPressState.timerId);
    longPressState = null;
  }

  function _suppressedClickKey(sessionId: string, seq: number, emoji: string): string {
    return sessionId + "#" + seq + "#" + emoji;
  }

  function _consumeSuppressedClick(sessionId: string, seq: number, emoji: string): boolean {
    if (!suppressedClick) return false;
    if (suppressedClick.key !== _suppressedClickKey(sessionId, seq, emoji)) return false;
    suppressedClick.remaining -= 1;
    if (suppressedClick.remaining <= 0) suppressedClick = null;
    return true;
  }

  function _startLongPress(chip: HTMLElement, messageEl: HTMLElement, sessionId: string, seq: number, emoji: string, event: PointerEvent): void {
    _cancelLongPress();
    longPressState = {
      anchorEl: chip,
      messageEl: messageEl,
      sessionId: sessionId,
      seq: seq,
      emoji: emoji,
      pointerId: typeof event.pointerId === "number" ? event.pointerId : null,
      startX: typeof event.clientX === "number" ? event.clientX : 0,
      startY: typeof event.clientY === "number" ? event.clientY : 0,
      timerId: window.setTimeout(function () {
        if (!longPressState) return;
        var current = longPressState;
        if (document.getSelection && document.getSelection() && document.getSelection()!.type === "Range") {
          _cancelLongPress();
          return;
        }
        suppressedClick = { key: _suppressedClickKey(current.sessionId, current.seq, current.emoji), remaining: 1 };
        openReactionParticipantPopover(current.messageEl, current.anchorEl, current.emoji, false);
        _cancelLongPress();
      }, longPressDelayMs),
    };
  }

  // ===== 토글(피커·칩 클릭 공용, §설계F) =====

  function requestReactionToggle(sessionId: string, seq: number, emoji: string): void {
    if (disabledSessions.has(sessionId)) return;
    var key = _makeKey(sessionId, seq);
    var st = _ensureState(key);
    var issuedAt = liveStamp;

    // 1) 스냅샷(롤백용) — {count, mine} 값 객체는 교체만 하고 직접 mutate하지 않으므로 얕은 clone으로 충분.
    var snapshot = new Map(st.reactions);

    // 2) 목표 상태 계산 후 즉시 렌더(로컬 예측 — 이벤트 경로 증감과 무관, 파일 헤더 주석 참고).
    var existing = st.reactions.get(emoji);
    var wasMine = existing ? existing.mine : false;
    var active = !wasMine;
    var nextCount = (existing ? existing.count : 0) + (active ? 1 : -1);
    var predicted = new Map(st.reactions);
    if (nextCount <= 0) predicted.delete(emoji);
    else predicted.set(emoji, {
      count: nextCount,
      mine: active,
      participants: existing ? existing.participants : [],
      hiddenCount: existing ? existing.hiddenCount : 0,
    });
    st.reactions = predicted;
    _renderChips(sessionId, seq);

    // 3) 목표상태 쓰기 엔드포인트로 전송(블라인드 토글이 아니라 active — 재시도 안전).
    _fetchJson("/api/rooms/sessions/" + encodeURIComponent(sessionId) + "/reactions", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ seq: seq, emoji: emoji, active: active }),
    }).then(function (body) {
      // 4) 성공 — 응답의 권위 집계로 치환(신선도 가드 통과 시). 곧 도착할 자기 에코도 같은 값이라
      //    화면이 흔들리지 않는다.
      var reactions = Array.isArray(body.reactions) ? body.reactions.filter(_isMrReactionEntry) : [];
      _applyIfFresh(sessionId, seq, issuedAt, reactions, true);
    }).catch(function (error: unknown) {
      var statusCode = _statusCodeOf(error);
      _maybeDisableFromStatus(sessionId, statusCode);
      // 5) 실패 — 스냅샷으로 롤백(신선도 가드 통과 시에만 — 그 사이 더 새 fan-out이 적용됐으면 건너뛴다).
      var key2 = _makeKey(sessionId, seq);
      var st2 = _ensureState(key2);
      if (st2.lastLiveStamp > issuedAt) return;
      st2.reactions = snapshot;
      _renderChips(sessionId, seq);
    });
  }

  // ===== 복원(§설계H) =====

  function _domSeqRange(sessionId: string): { min: number; max: number } | null {
    var container = _chatMessagesEl();
    if (!container) return null;
    var nodes = container.querySelectorAll<HTMLElement>("[data-msg-seq]");
    var min = Infinity;
    var max = -Infinity;
    nodes.forEach(function (node) {
      if (node.dataset.sessionId !== sessionId) return;
      var seq = Number(node.dataset.msgSeq);
      if (!Number.isInteger(seq)) return;
      if (seq < min) min = seq;
      if (seq > max) max = seq;
    });
    if (min === Infinity) return null;
    return { min: min, max: max };
  }

  function _splitRange(fromSeq: number, toSeq: number, maxSpan: number): Array<{ from: number; to: number }> {
    var chunks: Array<{ from: number; to: number }> = [];
    var start = fromSeq;
    while (start <= toSeq) {
      var end = Math.min(start + maxSpan, toSeq);
      chunks.push({ from: start, to: end });
      start = end + 1;
    }
    return chunks;
  }

  async function _fetchRangeAndApply(sessionId: string, fromSeq: number, toSeq: number): Promise<void> {
    var issuedAt = liveStamp;
    var chunks = _splitRange(fromSeq, toSeq, RANGE_CHUNK);
    for (var i = 0; i < chunks.length; i++) {
      // work-inline-cards.ts restore() 선례 미러: await 이후 세션 전환 여부를 재확인해, 이미 다른
      // 세션으로 넘어갔다면 남은 청크는 조회하지 않는다(cross-session-event-attribution 회귀 방지).
      if (typeof window !== "undefined" && window.currentSessionId !== sessionId) return;
      var chunk = chunks[i];
      try {
        var body = await _fetchJson(
          "/api/rooms/sessions/" + encodeURIComponent(sessionId) + "/reactions?fromSeq=" + chunk.from + "&toSeq=" + chunk.to,
        );
        if (typeof window !== "undefined" && window.currentSessionId !== sessionId) return;
        var messages = Array.isArray(body.messages) ? body.messages.filter(_isMrRecord) : [];
        messages.forEach(function (m) {
          if (!Number.isInteger(m.seq)) return;
          var reactions = Array.isArray(m.reactions) ? m.reactions.filter(_isMrReactionEntry) : [];
          _applyIfFresh(sessionId, m.seq as number, issuedAt, reactions, true);
        });
      } catch (error: unknown) {
        var statusCode = _statusCodeOf(error);
        _maybeDisableFromStatus(sessionId, statusCode);
        if (typeof statusCode === "number" && DISABLING_STATUSES.indexOf(statusCode) !== -1) return;
      }
    }
  }

  function restore(sessionId: string): void {
    if (!sessionId || disabledSessions.has(sessionId)) return;
    var range = _domSeqRange(sessionId);
    if (!range) return;
    void _fetchRangeAndApply(sessionId, range.min, range.max);
  }

  function restoreRange(sessionId: string, fromSeq: number, toSeq: number): void {
    if (!sessionId || disabledSessions.has(sessionId)) return;
    if (!Number.isInteger(fromSeq) || !Number.isInteger(toSeq) || fromSeq > toSeq) return;
    void _fetchRangeAndApply(sessionId, fromSeq, toSeq);
  }

  function reset(): void {
    _cancelLongPress();
    _closeParticipantPopover(false);
    suppressedClick = null;
    pendingParticipantFocus = null;
    participantFocusTransferKey = null;
    suppressParticipantFocusOpen = false;
    state.clear();
    _closePicker();
  }

  // ===== 피커(§설계E) =====

  function _positionPicker(anchorBtn: HTMLElement, pickerEl: HTMLElement): void {
    var area = _chatAreaEl();
    if (!area) return;
    var areaRect = area.getBoundingClientRect();
    var btnRect = anchorBtn.getBoundingClientRect();
    var left = btnRect.left - areaRect.left;
    left = Math.max(8, Math.min(left, areaRect.width - pickerEl.offsetWidth - 8));
    var top = btnRect.top - areaRect.top - pickerEl.offsetHeight - 6;
    if (top < 8) top = btnRect.bottom - areaRect.top + 6;
    pickerEl.style.left = left + "px";
    pickerEl.style.top = top + "px";
  }

  function _ensurePickerButtons(): void {
    if (pickerBuilt) return;
    var pickerEl = _pickerEl();
    if (!pickerEl) return;
    pickerBuilt = true;
    pickerEl.innerHTML = REACTION_EMOJIS.map(function (emoji) {
      return '<button type="button" class="reaction-picker-btn" role="menuitem" data-emoji="' + emoji + '" aria-label="' + emoji + ' 반응">' + emoji + '</button>';
    }).join("");
    pickerEl.addEventListener("click", function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var btn = target.closest<HTMLElement>(".reaction-picker-btn");
      if (!btn || !activePicker) return;
      var emoji = btn.dataset.emoji;
      var picked = activePicker;
      if (emoji) requestReactionToggle(picked.sessionId, picked.seq, emoji);
      _closePicker();
    });
  }

  function openReactionPicker(messageEl: HTMLElement, anchorBtn: HTMLElement): void {
    var sessionId = messageEl.dataset.sessionId;
    var seqRaw = messageEl.dataset.msgSeq;
    var seq = seqRaw !== undefined ? Number(seqRaw) : NaN;
    if (!sessionId || !Number.isInteger(seq)) return;
    if (disabledSessions.has(sessionId)) return;

    _ensurePickerButtons();
    var pickerEl = _pickerEl();
    if (!pickerEl) return;

    _closeParticipantPopover(false);
    activePicker = { sessionId: sessionId, seq: seq, anchorBtn: anchorBtn, messageEl: messageEl };
    pickerEl.hidden = false;
    _positionPicker(anchorBtn, pickerEl);
    anchorBtn.setAttribute("aria-expanded", "true");
    var actionsEl = messageEl.querySelector<HTMLElement>(".msg-actions");
    if (actionsEl) actionsEl.classList.add("pinned");
  }

  function _closePicker(): void {
    var pickerEl = _pickerEl();
    if (pickerEl) pickerEl.hidden = true;
    if (activePicker) {
      activePicker.anchorBtn.setAttribute("aria-expanded", "false");
      var actionsEl = activePicker.messageEl.querySelector<HTMLElement>(".msg-actions");
      if (actionsEl) actionsEl.classList.remove("pinned");
    }
    activePicker = null;
  }

  // ===== 클릭 위임 · 스크롤/외부클릭/Esc 닫힘 (§설계E) =====
  // chat.ts의 기존 delegated listener(quote/reply/artifact-card)를 건드리지 않고, #chatMessages에
  // 독립된 리스너를 하나 더 추가한다 — 같은 컨테이너에 여러 delegated listener가 공존하는 기존 관례
  // (chat.ts가 이미 2개를 건다)를 그대로 따른다.
  function _installListeners(): void {
    var chatMessagesEl = _chatMessagesEl();
    if (chatMessagesEl) {
      chatMessagesEl.addEventListener("click", function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;

        var triggerBtn = target.closest<HTMLElement>('.msg-act[data-act="reaction"]');
        if (triggerBtn) {
          var msgEl = triggerBtn.closest<HTMLElement>(".message");
          if (!msgEl) return;
          if (activePicker && activePicker.anchorBtn === triggerBtn) {
            _closePicker();
          } else {
            _closePicker();
            openReactionPicker(msgEl, triggerBtn);
          }
          return;
        }

        // 기존 칩 클릭은 피커 없이 곧바로 내 반응 토글이다(CAP32, §설계F).
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (chip) {
          var msgEl2 = chip.closest<HTMLElement>(".message");
          if (!msgEl2) return;
          var sessionId = msgEl2.dataset.sessionId;
          var seqRaw = msgEl2.dataset.msgSeq;
          var seq = seqRaw !== undefined ? Number(seqRaw) : NaN;
          var emoji = chip.dataset.emoji;
          if (sessionId && Number.isInteger(seq) && emoji && _consumeSuppressedClick(sessionId, seq, emoji)) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          if (sessionId && Number.isInteger(seq) && emoji) requestReactionToggle(sessionId, seq, emoji);
        }
      });

      chatMessagesEl.addEventListener("mouseover", function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (!chip) return;
        var related = event.relatedTarget;
        if (related instanceof Element && chip.contains(related)) return;
        var msgEl = chip.closest<HTMLElement>(".message");
        var sessionId = msgEl ? msgEl.dataset.sessionId : null;
        var seqRaw = msgEl ? msgEl.dataset.msgSeq : null;
        var seq = seqRaw !== null ? Number(seqRaw) : NaN;
        var emoji = chip.dataset.emoji;
        if (
          msgEl
          && sessionId
          && Number.isInteger(seq)
          && emoji
          && activeParticipantPopover
          && activeParticipantPopover.sessionId === sessionId
          && activeParticipantPopover.seq === seq
          && activeParticipantPopover.emoji === emoji
        ) {
          activeParticipantPopover.anchorEl = chip;
          activeParticipantPopover.messageEl = msgEl;
          _positionParticipantPopover(chip);
          return;
        }
        if (msgEl && emoji) openReactionParticipantPopover(msgEl, chip, emoji, false);
      });

      chatMessagesEl.addEventListener("mouseout", function (event) {
        if (!activeParticipantPopover) return;
        var target = event.target;
        if (!(target instanceof Element)) return;
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (!chip || chip !== activeParticipantPopover.anchorEl) return;
        var related = event.relatedTarget;
        if (related instanceof Element) {
          var popover = _participantPopoverEl();
          if (chip.contains(related) || (popover && popover.contains(related))) return;
        }
        _closeParticipantPopover(false);
      });

      chatMessagesEl.addEventListener("focusin", function (event) {
        if (suppressParticipantFocusOpen) {
          suppressParticipantFocusOpen = false;
          return;
        }
        var target = event.target;
        if (!(target instanceof Element)) return;
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (!chip) return;
        var msgEl = chip.closest<HTMLElement>(".message");
        var emoji = chip.dataset.emoji;
        if (!msgEl || !emoji) return;
        var sessionId = msgEl.dataset.sessionId;
        var seqRaw = msgEl.dataset.msgSeq;
        var seq = seqRaw !== undefined ? Number(seqRaw) : NaN;
        if (
          activeParticipantPopover
          && sessionId
          && Number.isInteger(seq)
          && activeParticipantPopover.sessionId === sessionId
          && activeParticipantPopover.seq === seq
          && activeParticipantPopover.emoji === emoji
        ) {
          activeParticipantPopover.restoreFocus = true;
          activeParticipantPopover.anchorEl = chip;
          activeParticipantPopover.messageEl = msgEl;
          return;
        }
        openReactionParticipantPopover(msgEl, chip, emoji, true);
      });

      chatMessagesEl.addEventListener("focusout", function (event) {
        if (!activeParticipantPopover) return;
        var target = event.target;
        var activePopoverKey = _suppressedClickKey(
          activeParticipantPopover.sessionId,
          activeParticipantPopover.seq,
          activeParticipantPopover.emoji,
        );
        var focusTransferPending = !!(
          pendingParticipantFocus && pendingParticipantFocus.key === activePopoverKey
        ) || participantFocusTransferKey === activePopoverKey;
        if (
          focusTransferPending
          && (
            target instanceof Element
              ? (
                !!target.closest<HTMLElement>(".msg-reaction")
                || !target.isConnected
              )
              : false
          )
        ) return;
        var related = event.relatedTarget;
        if (related instanceof Element) {
          var popover = _participantPopoverEl();
          if (activeParticipantPopover.anchorEl.contains(related) || (popover && popover.contains(related))) return;
        }
        _closeParticipantPopover(false);
      });

      chatMessagesEl.addEventListener("keydown", function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (!chip) return;
        if (event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey)) {
          var msgEl = chip.closest<HTMLElement>(".message");
          var emoji = chip.dataset.emoji;
          if (msgEl && emoji) {
            event.preventDefault();
            openReactionParticipantPopover(msgEl, chip, emoji, true);
          }
        }
      });

      chatMessagesEl.addEventListener("pointerdown", function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        var chip = target.closest<HTMLElement>(".msg-reaction");
        if (!chip) return;
        var msgEl = chip.closest<HTMLElement>(".message");
        var sessionId = msgEl ? msgEl.dataset.sessionId : null;
        var seqRaw = msgEl ? msgEl.dataset.msgSeq : null;
        var seq = seqRaw !== null ? Number(seqRaw) : NaN;
        var emoji = chip.dataset.emoji;
        if (!msgEl || !sessionId || !Number.isInteger(seq) || !emoji) return;
        suppressParticipantFocusOpen = true;
        queueMicrotask(function () {
          if (suppressParticipantFocusOpen) suppressParticipantFocusOpen = false;
        });
        if (event.pointerType !== "touch") return;
        _startLongPress(chip, msgEl, sessionId, seq, emoji, event);
      });

      chatMessagesEl.addEventListener("pointermove", function (event) {
        if (!longPressState) return;
        if (longPressState.pointerId !== null && event.pointerId !== longPressState.pointerId) return;
        if (
          Math.abs(event.clientX - longPressState.startX) > longPressThresholdPx
          || Math.abs(event.clientY - longPressState.startY) > longPressThresholdPx
        ) _cancelLongPress();
      });

      chatMessagesEl.addEventListener("pointerup", function () { _cancelLongPress(); });
      chatMessagesEl.addEventListener("pointercancel", function () { _cancelLongPress(); });

      // 피커가 스크롤 컨테이너 밖에 호스팅되므로 스크롤 시 앵커와 분리된다 — 닫는다.
      chatMessagesEl.addEventListener("scroll", function () {
        _cancelLongPress();
        _closePicker();
        _closeParticipantPopover(false);
      }, { passive: true });
    }

    document.addEventListener("click", function (event) {
      if (!activePicker) return;
      var target = event.target;
      if (!(target instanceof Element)) return;
      var pickerEl = _pickerEl();
      if (pickerEl && pickerEl.contains(target)) return;
      if (target.closest('.msg-act[data-act="reaction"]')) return;
      _closePicker();
    });

    document.addEventListener("click", function (event) {
      if (!activeParticipantPopover) return;
      var target = event.target;
      if (!(target instanceof Element)) return;
      var popover = _participantPopoverEl();
      if (popover && popover.contains(target)) return;
      if (target.closest(".msg-reaction")) return;
      _closeParticipantPopover(false);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && activePicker) _closePicker();
      if (event.key === "Escape" && activeParticipantPopover) {
        event.preventDefault();
        _closeParticipantPopover(true);
      }
    });

    document.addEventListener("selectionchange", function () {
      if (document.getSelection && document.getSelection() && document.getSelection()!.type === "Range") _cancelLongPress();
    });
  }

  _installListeners();

  // 공개 표면은 §설계D가 정한 5개뿐이다 — 토글/피커 열기는 DOM 클릭(칩·트리거 버튼)을 통해서만
  // 진입한다(_installListeners). 테스트도 실제 click 이벤트를 dispatch해 이 경로를 그대로 탄다
  // (room-context-menu.test.ts 관례 미러).
  return {
    REACTION_EMOJIS: REACTION_EMOJIS,
    onMessageReaction: onMessageReaction,
    restore: restore,
    restoreRange: restoreRange,
    reset: reset,
    closePicker: _closePicker,
    closeParticipantPopover: function () { _closeParticipantPopover(false); },
  };
})();
